import React, { useState } from 'react';
import { observer } from 'mobx-react-lite';
import { Dialog, DialogTitle, DialogContent, DialogActions } from '@mui/material';
import { portfolioStore } from '../../stores/portfolio-store';
import { Button, Input } from '@privemind/components';
import { AssetConflictData } from '../../types';
import './asset-conflict-modal.scss';

interface AssetConflictModalProps {
  conflictData: AssetConflictData;
  onClose: () => void;
  onSuccess: () => void;
}

export const AssetConflictModal: React.FC<AssetConflictModalProps> = observer(({
  conflictData,
  onClose,
  onSuccess
}) => {
  const [incrementAmount, setIncrementAmount] = useState(conflictData.amount.toString());

  const handleReplace = async () => {
    try {
      const result = await portfolioStore.replaceAssetInBucket(conflictData.id, conflictData);
      
      if (result instanceof Error) {
        alert(`Failed to replace asset: ${result.message}`);
        return;
      }
      
      if (result) {
        onSuccess();
        onClose();
      } else {
        alert('Failed to replace asset');
      }
    } catch (error) {
      alert('Failed to replace asset');
    }
  };

  const handleIncrement = async () => {
    const amount = parseFloat(incrementAmount);
    
    if (isNaN(amount)) {
      alert('Please enter a valid increment amount');
      return;
    }
    
    const incrementData: { amount: number; avgBuyPrice?: number } = {
      amount: amount
    };
    
    // Add avgBuyPrice for weighted average calculation if provided
    if (conflictData.avgBuyPrice !== undefined) {
      incrementData.avgBuyPrice = conflictData.avgBuyPrice;
    }
    
    try {
      const result = await portfolioStore.incrementAssetInBucket(conflictData.id, conflictData.bucket, incrementData);
      
      if (result instanceof Error) {
        if (amount < 0) {
          alert('Cannot reduce amount below zero');
        } else {
          alert(`Failed to increment asset: ${result.message}`);
        }
        return;
      }
      
      if (result) {
        onSuccess();
        onClose();
      } else {
        if (amount < 0) {
          alert('Cannot reduce amount below zero');
        } else {
          alert('Failed to increment asset');
        }
      }
    } catch (error) {
      alert('Failed to increment asset');
    }
  };

  return (
    <Dialog open={true} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Asset Already Exists in Bucket</DialogTitle>
      <DialogContent>
        <div className="asset-conflict-modal">
          <p className="asset-conflict-modal__message">
            {conflictData.name} ({conflictData.symbol}) already exists in the selected bucket.
          </p>
          
          <div className="asset-conflict-modal__options">
            <div className="asset-conflict-modal__option">
              <h4>Replace</h4>
              <p>Replace the existing asset with the new values</p>
              <Button onClick={handleReplace}>
                Replace Asset
              </Button>
            </div>
            
            <div className="asset-conflict-modal__option">
              <h4>Increment</h4>
              <p>Add/subtract from the existing amount and calculate new weighted average price</p>
              <div className="asset-conflict-modal__increment-input">
                <label>Amount to add/subtract</label>
                <Input
                  value={incrementAmount}
                  onChange={setIncrementAmount}
                  placeholder="e.g., 1.5 or -0.5"
                />
              </div>
              <Button onClick={handleIncrement}>
                Increment Asset
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
      <DialogActions>
        <Button color="secondary" onClick={onClose}>
          Cancel
        </Button>
      </DialogActions>
    </Dialog>
  );
});
